import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ThemeManager } from '@/lib/theme-manager';
import { useBranding } from '@/hooks/useBranding';
import { API_URL } from '@/lib/api-client';

type AuthPage = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'pending-approval';

interface SignupPageProps {
  onNavigate: (page: AuthPage) => void;
  onSignupSuccess: () => void;
}

export function SignupPage({ onNavigate, onSignupSuccess }: SignupPageProps) {
  const { branding } = useBranding();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    ThemeManager.getInstance().loadTheme();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          displayName: displayName || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      if (data.requiresApproval) {
        onNavigate('pending-approval');
      } else {
        onSignupSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-charcoal-950 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Branding Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-royal-gradient flex items-center justify-center shadow-glow-lg">
            <img
              src={branding.logoUrl}
              alt={branding.appName}
              className="w-10 h-10 rounded-xl object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-3xl font-bold text-royal-gradient mb-2 font-heading">
            {branding.appName}
          </h1>
          <p className="text-slate-400">{branding.tagline}</p>
        </div>

        {/* Form Card */}
        <div className="glass rounded-2xl p-8 shadow-glass">
          <h2 className="text-xl font-semibold text-white mb-6">Create Account</h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="signup-email" className="block text-sm text-slate-400 mb-2">
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg bg-charcoal-800 border border-charcoal-700
                         text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                         transition-all duration-200"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="signup-name" className="block text-sm text-slate-400 mb-2">
                Display Name <span className="text-slate-600">(optional)</span>
              </label>
              <input
                id="signup-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-charcoal-800 border border-charcoal-700
                         text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                         transition-all duration-200"
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="signup-password" className="block text-sm text-slate-400 mb-2">
                Password
              </label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-lg bg-charcoal-800 border border-charcoal-700
                         text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                         transition-all duration-200"
                placeholder="Min 8 characters"
              />
            </div>

            <div>
              <label htmlFor="signup-confirm" className="block text-sm text-slate-400 mb-2">
                Confirm Password
              </label>
              <input
                id="signup-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-lg bg-charcoal-800 border border-charcoal-700
                         text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                         transition-all duration-200"
                placeholder="Repeat password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg bg-royal-gradient text-white font-medium
                       hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2
                       focus:ring-offset-charcoal-900 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200 shadow-glow"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => onNavigate('login')}
              className="text-sm text-slate-400 hover:text-amber-400 transition-colors"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Your data is encrypted and stored securely. We never share your personal information.
        </p>
      </motion.div>
    </div>
  );
}
